import { useWindowWidth } from "@react-hook/window-size";
import { useEffect, useState } from "react";

import { useThemeUI } from "@theme/components/ThemeUi";

export interface NavHeightInterface {
  /** height of nav + super banner + category bar (on desktop) */
  currentNavHeight: number;
  // rarely we need to get the super banner height from this hook
  // so that we can get the current height and animate that content
  currentSuperBannerHeight: number;
  /** can be used as responsive array for sx props */
  navHeights: number[];
  /** can be used as responsive array for sx props */
  superBannerHeights: number[];
}

/**
 * Use Nav Height
 *
 * this hook makes it easy to calculate the various heights of the nav
 * It is useful for absolutely positioning various items when the nav is in its various states
 *
 * @returns
 */
export const useNavHeight = (isRestrictedPage?: boolean): NavHeightInterface => {
  const { theme } = useThemeUI();
  const breakpoints = theme.breakpoints as string[];

  // these are all zero values for when nav features should be hidden
  const navHiddenHeights = theme.sizes.navHiddenHeight;

  const superBannerHeights = isRestrictedPage ? navHiddenHeights : theme.sizes.superBannerHeight;

  const categoryBarHeights = isRestrictedPage ? navHiddenHeights : theme.sizes.categoryBarHeight;

  const navBarHeights = theme.sizes.navBarHeight;

  const navHeights = breakpoints.map((_, index: number) => {
    const navBarHeight = navBarHeights[index];
    const categoryBarHeight = categoryBarHeights[index];
    const superBannerHeight = superBannerHeights[index];

    return navBarHeight + categoryBarHeight + superBannerHeight;
  });

  // default current breakpoint at mobile version of navigation (produces less initial layout shift)
  const [currentBreakpoint, setCurrentBreakpoint] = useState(0);

  const initialWidth = parseInt(breakpoints[0]);

  // Set initial window width to last entry of breakpoints which contains the tallest navigation,
  // in case we get 0 returned when nothing has loaded
  const windowWidth = useWindowWidth({ initialWidth: initialWidth });

  // Find closest matching breakpoint and set value
  useEffect(() => {
    const found = breakpoints.find((point) => parseInt(point) > windowWidth);
    const index = found ? breakpoints.indexOf(found) : breakpoints.length - 1;
    setCurrentBreakpoint(index);
  }, [breakpoints, windowWidth]);

  return {
    currentNavHeight: navHeights[currentBreakpoint],
    currentSuperBannerHeight: superBannerHeights[currentBreakpoint],
    navHeights: navHeights,
    superBannerHeights: superBannerHeights,
  };
};
